/**
 * The reception room, table by table (D73).
 *
 * Traced from the planner's seating chart (Videet, 21 Oct 2026), then
 * redrawn as a diagram, not to scale. The chart is mostly floor: on a phone
 * its tables come out the size of a fingernail. So the gaps close, the bar
 * becomes an arc in its corner, the doors become arrows, and the DJ booth is
 * left out. What is kept is every table's neighbours, its row and its side of
 * the dance floor, so a guest looking for "table 8" finds it where the staff's
 * printout has it.
 * The rows above and below the dance floor are even zigzags and the two long
 * tables are one size, for legibility, not because the room is. The chairs
 * are left out, because who sits in which chair is not something the site
 * says.
 *
 * Units are the drawing's own; nothing here is a distance in the room.
 *
 * **The names are the roster's.** `roster-seed.csv` writes a table as
 * "Empire State (8)" and the couple's as "Couple's Table"; `tableFor` reads
 * either shape. If a table is renamed, rename it here and in the CSV together,
 * or that table's guests lose their highlight.
 */

export interface SeatTable {
  /** The planner's number, which is what is printed on the table. */
  no: number;
  name: string;
  /** The name as drawn, one entry per line. A round table is 140 units across. */
  lines: string[];
  shape: 'round' | 'long';
  /** Centre, for a round table; the top-left corner, for a long one. */
  x: number;
  y: number;
  w?: number;
  h?: number;
}

export const ROOM = { w: 1370, h: 1200 } as const;

const R = 70;
export const ROUND_R = R;

export const TABLES: readonly SeatTable[] = [
  { no: 1, name: 'Brooklyn Bridge', lines: ['Brooklyn', 'Bridge'], shape: 'round', x: 292, y: 920 },
  { no: 2, name: 'Fort Greene Park', lines: ['Fort Greene', 'Park'], shape: 'round', x: 457, y: 1100 },
  { no: 3, name: 'DUMBO', lines: ['DUMBO'], shape: 'round', x: 622, y: 920 },
  { no: 4, name: 'Central Park', lines: ['Central', 'Park'], shape: 'round', x: 787, y: 1100 },
  { no: 5, name: 'Flatiron', lines: ['Flatiron'], shape: 'round', x: 952, y: 920 },
  { no: 6, name: 'Wall Street', lines: ['Wall', 'Street'], shape: 'round', x: 1117, y: 1100 },
  { no: 7, name: 'The Met', lines: ['The Met'], shape: 'long', x: 590, y: 690, w: 320, h: 70 },
  { no: 8, name: 'Empire State', lines: ['Empire State'], shape: 'long', x: 590, y: 450, w: 320, h: 70 },
  { no: 9, name: 'Lady Liberty', lines: ['Lady', 'Liberty'], shape: 'round', x: 1030, y: 725 },
  { no: 10, name: 'Manhattan Bridge', lines: ['Manhattan', 'Bridge'], shape: 'round', x: 1200, y: 605 },
  { no: 11, name: 'Trinity Church', lines: ['Trinity', 'Church'], shape: 'round', x: 1030, y: 485 },
  { no: 12, name: 'Grand Central', lines: ['Grand', 'Central'], shape: 'round', x: 787, y: 280 },
  { no: 13, name: 'Lincoln Center', lines: ['Lincoln', 'Center'], shape: 'round', x: 622, y: 100 },
  { no: 14, name: 'Chrysler Building', lines: ['Chrysler', 'Building'], shape: 'round', x: 457, y: 280 },
  { no: 15, name: 'WTC', lines: ['WTC'], shape: 'round', x: 292, y: 100 },
];

/** Not numbered, and listed apart so nothing above has to allow for that. */
export const COUPLE = { name: "Couple's Table", x: 40, y: 535, w: 110, h: 110 } as const;

/** Everything in the room that is not somewhere to sit. */
export const DANCE_FLOOR = { x: 170, y: 380, w: 390, h: 420 } as const;
/** A quarter circle in the top-right corner: centred on the corner, so only
 *  the quarter inside the room is drawn. */
export const BAR = { r: 300 } as const;
/** The two doors, on the right-hand wall, drawn as arrows pointing in. */
export const ENTRANCES = [{ y: 435 }, { y: 795 }] as const;

/** The centre of a table, round or long: where its label sits, and where the
 *  expanded plan scrolls to. */
export const centreOf = (t: SeatTable): [number, number] =>
  t.shape === 'round' ? [t.x, t.y] : [t.x + t.w! / 2, t.y + t.h! / 2];

const fold = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The table a roster entry names, or `'couple'`, or null.
 *
 * By the number in brackets when there is one ("Empire State (8)"), since a
 * number cannot be misspelt into another table's; by name otherwise. A name
 * and a number that disagree are not guessed between: that is a typo in the
 * CSV, and a highlight on the wrong table is worse than none.
 */
export function tableFor(roster: string | undefined): SeatTable | 'couple' | null {
  const raw = roster?.trim();
  if (!raw) return null;
  const m = /^(.*?)\s*\((\d+)\)\s*$/.exec(raw);
  const name = fold(m ? m[1]! : raw);
  if (name === fold(COUPLE.name) || name === 'couple') return 'couple';
  const byNo = m ? TABLES.find((t) => t.no === Number(m[2])) : undefined;
  const byName = TABLES.find((t) => fold(t.name) === name);
  if (byNo && name && byName !== byNo) return null;
  return byNo ?? byName ?? null;
}
